import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import Error from 'next/error';
import { ParsedUrlQuery } from 'querystring';
import React from 'react';
import ContentPage from '../../../components/templates/content-page/content-page';
import ProductDetailPage from '../../../components/templates/product-detail-page/product-detail-page';
import getContentPage from '../../../helpers/get-content-page';
import getErrorResponse from '../../../helpers/get-error-response';
import useLogoutAfterInactivity from '../../../hooks/use-logout-after-inactivity/use-logout-after-inactivity';
import ContentPageDocumentTypeEnum from '../../../models/content-page-document-type-enum';
import { ContentPageType } from '../../../models/content-page-type';
import { ProductDetailResultType } from '../../../models/product-detail-result-type';
import withCache from '../../../services/cache/with-cache';
import getCategoryStructure from '../../../services/content-api/endpoints/category/structure';
import productGetByPath from '../../../services/content-api/endpoints/product/variants/get-by-path';
import HttpException from '../../../services/exceptions/http-exception';
import { ProductStockItemType } from '../../../models/product-stock-item-type';
import searchStock from '../../../services/search-api/endpoints/search/stock';

interface Props {
  readonly page: ContentPageType;
  readonly productDetailResult: ProductDetailResultType;
  readonly stockQuantity: number | null;
  readonly statusCode: number;
}

const CategoryLandingVPage: NextPage<Props> = ({
  page,
  productDetailResult,
  stockQuantity,
  statusCode,
}: Props) => {
  useLogoutAfterInactivity(page);

  if (statusCode) {
    return <Error statusCode={statusCode} />;
  }

  if (page.documentType === ContentPageDocumentTypeEnum.ProductDetailPage) {
    return (
      <ProductDetailPage
        page={page}
        productDetailResult={productDetailResult}
        stockQuantity={stockQuantity}
      />
    );
  }

  return <ContentPage {...page} />;
};

const getStockForProductResult = async (
  productResult: ProductDetailResultType
): Promise<ProductStockItemType | null> => {
  if (!productResult?.product?.properties?.internalRef) {
    return null;
  }

  const stockResults = await searchStock([
    productResult.product.properties.internalRef,
  ]);

  return stockResults?.[0] ?? null;
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext<ParsedUrlQuery>
) => {
  try {
    const categoryStructure = await withCache('categoryStructure', () =>
      getCategoryStructure()
    );

    // There is an issue where Next.js is not parsing the parameters correctly therefore we
    // need to update the object to consider the incorrect property name that is being provided
    // 'amp;variantSku': '458 011 294' is being returned instead of 'variantSku': '458 011 294',

    context.query = {
      ...context.query,
      variantSku: context.query.variantSku ?? context.query['amp;variantSku'],
    };

    // If the required parameters for the product detail page aren't provided, throw a 404 to avoid a 500.

    if (!context.query.variantSku || !context.query.sku) {
      throw new HttpException(404);
    }

    const productDetailResult = await productGetByPath(
      categoryStructure.catalogId,
      ((context.query.slug as string[]) || []).join('/'),
      context.query.sku as string,
      context.query.variantSku as string
    );

    if (productDetailResult === null) {
      throw new HttpException(404);
    }

    const productStockResult = await getStockForProductResult(
      productDetailResult
    );
    const stockQuantity = productStockResult?.freeStock ?? null;

    const page = await getContentPage(
      categoryStructure.productPath,
      Boolean(context.query.token ?? false),
      context.query.token ?? ''
    );

    return {
      props: {
        page,
        productDetailResult,
        stockQuantity,
      },
    };
  } catch (error) {
    if (error.response?.status === 404 || error.status === 404)
      return { notFound: true };

    return getErrorResponse(error);
  }
};

export default CategoryLandingVPage;
